import { Row, Col, Button, Input, Select, Form, Modal, notification } from "antd";
import { SearchOutlined, EditOutlined, DeleteOutlined, FileTextOutlined } from "@ant-design/icons";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import styled from "@emotion/styled";
import { useSelector, useDispatch } from "react-redux";
import {
  getMenuList,
  getUserInfo,
  getUserList,
  getUserRoleId,
  getAccountsList,
  getSelectedAccount,
  getAccountsSearchText,
  getAccountsSelectedType,
} from "redux/selectors";
import {
  setAccountsList,
  setSelectedAccount,
  setAccountsFilter,
} from "redux/actions";
import { useState, useMemo, useEffect } from "react";
import Table from "atoms/Table";
import AccountModal from "./AccountModal";
import AccountDetail from "./AccountDetail";
import AccountReport from "./AccountReport";
import { currencyCodeMap, currencySymbolMap } from "common/Constants";
import queryKeys from "common/queryKey";
import { useMutate } from "hooks/useMutate";
import { useFetch } from "hooks/useFetch";
import { useUpdate } from "hooks/useUpdate";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";

const fmt = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return n % 1 === 0 ? String(Math.trunc(n)) : n.toString();
};

const { Option } = Select;

const StyledButton = styled(Button)`
  width: 150px;
  color: #fff !important;
  background-color: #00685F;
  border-color: #00685F;
  border-radius: 5px;
  &:hover,
  &:focus,
  &:active,
  &.active {
    background-color: #00685F !important;
    border-color: #00685F !important;
    color: #fff !important;
  }
`;

const ReportButton = styled(Button)`
  width: 150px;
  color: #333;
  background-color: white;
  border-color: #333;
  border-radius: 5px;
  margin-right: 10px;
  &:hover, &:focus {
    border-color: #000 !important;
  }
  &.ant-btn span {
    display: inline-flex;
  }
`;

const StyledInput = styled(Input)`
  width: 200px;
  &.ant-input-affix-wrapper { border-radius: 15px !important; }
  .ant-input-prefix { color: #aaa; }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content { border-radius: 10px; }
  .ant-modal-body { padding: 24px 10px 24px 10px; }
`;

const StyledDeleteModal = styled(Modal)`
  .ant-modal-content { border-radius: 10px; }
  .ant-modal-body { padding: 5px 10px 24px 10px; }
`;

const ReportModal = styled(Modal)`
  .ant-modal-content { border-radius: 12px; }
  .ant-modal-body { padding: 14px 24px 24px 24px; }
  .ant-modal-header { border-bottom: none; padding-top: 22px; border-radius: 12px 12px 0 0; }
  .ant-modal-title { font-weight: 600; font-size: 16px; }
  .ant-modal-footer { border-top: none; padding-bottom: 20px; }
  .ant-modal-close-x { font-size: 15px; line-height: 68px; }
`;

const TotalLabel = styled.span`
  font-size: 14px;
  color: #1F243B;
  font-weight: 400;
`;

const ActionIconsCell = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

const ClientSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    border-radius: 8px !important;
    height: 40px !important;
    align-items: center;
    display: flex !important;
  }
  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
    border-color: #52c6b0 !important;
    box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

const GenerateBtn = styled(Button)`
  border-radius: 6px;
  width: 100px;
  height: 38px;
  background-color: #00685F;
  border-color: #00685F;
  color: white !important;
  &:hover, &:focus {
    background-color: #00685F !important;
    border-color: #C2FAE5 !important;
    color: white !important;
  }
  &:disabled {
    background-color: lightgrey !important;
    border-color: #C2FAE5 !important;
    color: black !important;
  }
`;

const CancelBtn = styled(Button)`
  border-radius: 6px;
  width: 100px;
  height: 38px;
  &:hover, &:focus {
    border-color: black !important;
  }
`;

const HoverStyle = () => (
  <style>{`.ant-table-row:hover .action-icons { opacity: 1 !important; }`}</style>
);

const headerstyle = { fontSize: "20px", fontWeight: "600", color: "#1F243B" };
const ACCOUNT_TYPE_OPTIONS = ["Firm Account", "Client Account"];

const Accounts = () => {
  const dispatch = useDispatch();

  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const tableData = useSelector(getAccountsList);
  const selectedAccount = useSelector(getSelectedAccount);
  const searchText = useSelector(getAccountsSearchText);
  const selectedType = useSelector(getAccountsSelectedType);

  const Menulist = menuList[userRoleId] || Object.values(menuList)[0];

  useEffect(() => {
    return () => {
      dispatch(setSelectedAccount(null));
    };
  }, []);

  const [form] = Form.useForm();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteRecord, setPendingDeleteRecord] = useState(null);

  const [reportPickerOpen, setReportPickerOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showReport, setShowReport] = useState(false);

  const isEditMode = !!editingRecord;

  const {
    addAccount, updateAccount, deleteAccount,
    getAccounts, getAllCurrencyList, getAllClientList,
    generateAccountReport,
  } = queryKeys;

  const {
    isLoading: isFetching,
    refetch: refetchAccounts,
  } = useFetch(getAccounts.key, getAccounts.url, {
    onSuccess: (data) => {
      const list = data?.response?.data ?? [];
      dispatch(setAccountsList(list));
    },
  });

  const { data: currencyData } = useFetch(getAllCurrencyList.key, getAllCurrencyList.url);
  const currencyList = currencyData?.response?.data?.[0] ?? {};

  const currencyOptions = Object.keys(currencyList)
    .filter((key) => !["id", "date", "updated_at", "updated_currency"].includes(key))
    .map((key) => ({
      code: currencyCodeMap[key],
      label: key.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
      value: parseFloat(currencyList[key]),
    }));

  const CURRENCY_OPTIONS = currencyOptions.map((item) => item.code);

  const { data: clientData } = useFetch(getAllClientList.key, getAllClientList.url);
  const clientList = clientData?.response?.data ?? [];

  const {
    isLoading: isReportLoading,
    refetch: refetchReport,
  } = useFetch(
    selectedClientId ? `${generateAccountReport.key}_${selectedClientId}` : null,
    selectedClientId ? `${generateAccountReport.url}?clientid=${selectedClientId}` : null,
    { enabled: false }
  );

  const { mutate: createMutate, isLoading: isCreating } = useMutate(addAccount.key, addAccount.url);
  const { mutate: updateMutate, isLoading: isUpdating } = useUpdate(updateAccount.key, updateAccount.url);
  const { mutate: deleteMutate, isLoading: isDeleting } = useMutate(deleteAccount.key, deleteAccount.url);

  const [typeFilterForm] = Form.useForm();

  const filteredData = useMemo(() => {
    return tableData.filter((item) => {
      const matchesSearch =
        !searchText ||
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchText.toLowerCase())
        );

      const matchesType =
        !selectedType ||
        selectedType.length === 0 ||
        selectedType.includes(item.accounttype);

      return matchesSearch && matchesType;
    });
  }, [searchText, selectedType, tableData]);

  const accountTypes = useMemo(
    () => [...new Set(tableData.map((d) => d.accounttype))],
    [tableData]
  );

  const handleSearchChange = (e) => {
    dispatch(setAccountsFilter("searchText", e.target.value));
  };


  const handleRowClick = (record) => {
    dispatch(setSelectedAccount(record));
  };

  const handleBack = () => {
    dispatch(setSelectedAccount(null));
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record, e) => {
    e.stopPropagation();
    setEditingRecord(record);
    form.setFieldsValue({
      accountName: record.accountname,
      accountType: record.accounttype,
      accountNumber: record.accountnumber || "",
      accountHolderName: record.accountholdername || "",
      branchName: record.branchname || "",
      branchCode: record.branchcode || "",
      currency: record.currency,
    });
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    const payload = {
      accountname: values.accountName,
      accounttype: values.accountType,
      accountnumber: values.accountNumber || null,
      accountholdername: values.accountHolderName || null,
      branchname: values.branchName || null,
      branchcode: values.branchCode || null,
      currency: values.currency,
      balance: 0,
      ...(isEditMode
        ? { updatedby: userInfo?.userid }
        : { createdby: userInfo?.userid }),
      ...(isEditMode && { accountid: editingRecord?.accountid }),
    };

    if (isEditMode) {
      updateMutate(
        { id: editingRecord.key, ...payload },
        {
          onSuccess: (res) => {
            notification.success({ message: res?.message || "Account updated successfully" });
            refetchAccounts();
            setModalOpen(false);
            setEditingRecord(null);
            form.resetFields();
          },
          onError: (err) => {
            notification.error({ message: err?.message || "Failed to update account." });
          },
        }
      );
    } else {
      createMutate(payload, {
        onSuccess: (res) => {
          notification.success({ message: res?.message || "Account created successfully" });
          refetchAccounts();
          setModalOpen(false);
          form.resetFields();
        },
        onError: (err) => {
          notification.error({ message: err?.message || "Failed to create account." });
        },
      });
    }
  };

  const handleDelete = (record, e) => {
    e.stopPropagation();
    if (record.balance && Number(record.balance) > 0) {
      setDeleteErrorOpen(true);
    } else {
      setPendingDeleteRecord(record);
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDelete = () => {
    deleteMutate(
      { accountid: pendingDeleteRecord.accountid },
      {
        onSuccess: (res) => {
          notification.success({ message: res?.message || "Account deleted successfully!" });
          refetchAccounts();
          setDeleteConfirmOpen(false);
          setPendingDeleteRecord(null);
        },
        onError: (err) => {
          notification.error({ message: err?.message || "Failed to delete account." });
          setDeleteConfirmOpen(false);
          setPendingDeleteRecord(null);
        },
      }
    );
  };

  const openReportPicker = () => {
    setSelectedClientId(null);
    setReportPickerOpen(true);
  };

  const handleGenerateReport = async () => {
    if (!selectedClientId) {
      notification.warning({ message: "Please select a client first." });
      return;
    }

    setIsGenerating(true);
    try {
      const result = await refetchReport();
      const data = result?.data?.response ?? {};

      setReportData({
        reportRes: data?.reportRes ?? [],
        clientRes: data?.clientRes ?? [],
      });

      setReportPickerOpen(false);
      setShowReport(true);
    } catch (err) {
      notification.error({ message: err?.message || "Failed to generate report." });
    } finally {
      setIsGenerating(false);
    }
  };

  const canCreate = Menulist?.find(
    (item) =>
      item?.key === "accounts" && item?.permission?.create
  );

  const columns = [
    { title: "Account Name", dataIndex: "accountname", key: "accountname", width: "20%" },
    { title: "Account Type", dataIndex: "accounttype", key: "accounttype", width: "18%" },
    { title: "Currency", dataIndex: "currency", key: "currency", width: "12%" },
    {
      title: "Balance", dataIndex: "balance", key: "balance", width: "12%",
      render: (v, record) => {
        if (v == null) return "---";
        const symbol = currencySymbolMap[record.currency] ?? record.currency ?? "$";
        return `${symbol}${fmt(v)}`;
      },
    },
    {
      title: "Created by",
      dataIndex: "createdby",
      key: "createdby",
      width: "20%",
      render: (text, record) => {
        const finduser = userList?.find((item) => item?.userid === text);
        const formatDate = (dateStr) => {
          if (!dateStr) return "";
          const date = new Date(dateStr);
          const day = date.getDate();
          const month = date.toLocaleString("en-GB", { month: "short" });
          const year = date.getFullYear();
          const suffix =
            day % 10 === 1 && day !== 11 ? "st" :
              day % 10 === 2 && day !== 12 ? "nd" :
                day % 10 === 3 && day !== 13 ? "rd" : "th";
          return `${day}${suffix} ${month} ${year}`;
        };
        return (
          <span style={{ whiteSpace: "pre-line" }}>
            {finduser?.fullname ? `${finduser.fullname};\n` : ""}
            {formatDate(record.createdat)}
          </span>
        );
      },
    },
    {
      title: "", key: "actions", width: "10%",
      render: (_, record) => (
        <ActionIconsCell className="action-icons">
          {Menulist?.find(
            (item) =>
              item?.key === "accounts" &&
              item?.permission?.edit
          ) && (
              <EditOutlined
                style={{ fontSize: 16, background: "#deefee", color: "#00685F", cursor: "pointer", padding: 8, borderRadius: 5 }}
                onClick={(e) => openEditModal(record, e)}
              />
            )}
          {Menulist?.find(
            (item) =>
              item?.key === "accounts" &&
              item?.permission?.delete
          ) && (
              <DeleteOutlined
                style={{ fontSize: 16, background: "#deefee", color: "#00685F", cursor: "pointer", padding: 8, borderRadius: 5 }}
                onClick={(e) => handleDelete(record, e)}
              />
            )}
        </ActionIconsCell>
      ),
    },
  ];

  if (selectedAccount) {
    return <AccountDetail account={selectedAccount} onBack={handleBack} refetchAccounts={refetchAccounts} />;
  }

  return (
    <>
      <HoverStyle />

      <Row className="page-title" align="middle" style={{ marginBottom: 12 }}>
        <Col sm={24} md={12} style={headerstyle}>Account</Col>
        <Col sm={24} md={12} className="align-right" style={{ textAlign: "right" }}>
          <ReportButton icon={<FileTextOutlined />} onClick={openReportPicker}>
            Generate Report
          </ReportButton>
          {canCreate && (
            <StyledButton onClick={openCreateModal}>
              Create Account
            </StyledButton>
          )}
        </Col>
      </Row>

      <br />

      <Row align="middle" gutter={[12, 8]} style={{ marginBottom: 12, paddingBottom: 10 }}>
        <Col flex="none"><TotalLabel>Total Accounts: {tableData.length}</TotalLabel></Col>
        <Col flex="auto" />
        <Col flex="none">
          <StyledInput
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearchChange}
            allowClear
          />
        </Col>
        <Col flex="none">
          <Form
            form={typeFilterForm}
            onValuesChange={(changedValues) => {
              if ("accounttype" in changedValues) {
                const val = changedValues.accounttype ?? [];
                dispatch(setAccountsFilter("selectedType", val.length ? val : null));
              }
            }}
          >
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginTop: 15 }}>
              <div style={{ width: 190 }}>
                <DropdownCheckBox
                  name="accounttype"
                  label="Type"
                  form={typeFilterForm}
                  options={accountTypes.map((type) => ({ label: type, value: type }))}
                  handleFormChange={() => { }}
                />
              </div>
            </div>
          </Form>
        </Col>
      </Row>

      <Table
        dataSource={filteredData}
        columns={columns}
        pagination={true}
        loading={isFetching}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
      />

      <AccountModal
        open={modalOpen}
        isEditMode={isEditMode}
        form={form}
        submitting={isCreating || isUpdating}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        accountTypeOptions={ACCOUNT_TYPE_OPTIONS}
        currencyOptions={CURRENCY_OPTIONS}
        editingBalance={editingRecord?.balance ?? 0}
        existingAccountNames={tableData.map((a) => a.accountname?.trim().toLowerCase())}
        editingAccountName={editingRecord?.accountname ?? null}
      />

      <ReportModal
        open={reportPickerOpen}
        onCancel={() => { if (!isGenerating) setReportPickerOpen(false); }}
        footer={null}
        width={500}
        centered
        title="Choose the client who you want to generate the report for"
        closable={!isGenerating}
      >
        <div style={{ padding: "4px 6px 0" }}>
          <ClientSelect
            placeholder="Select client"
            value={selectedClientId}
            onChange={(val) => setSelectedClientId(val)}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {clientList.map((c) => {
              const name =
                c.clienttype === "company"
                  ? c.companyregisteredname || c.clientname
                  : c.clientname || c.companyregisteredname;
              return <Option key={c.bdo_id} value={c.bdo_id}>{name}</Option>;
            })}
          </ClientSelect>

          <div style={{ display: "flex", justifyContent: "right", gap: 12, marginTop: 40 }}>
            <CancelBtn onClick={() => setReportPickerOpen(false)} disabled={isGenerating}>
              Cancel
            </CancelBtn>
            <GenerateBtn
              onClick={handleGenerateReport}
              loading={isGenerating || isReportLoading}
              disabled={!selectedClientId}
            >
              Generate
            </GenerateBtn>
          </div>
        </div>
      </ReportModal>

      <StyledModal
        open={deleteErrorOpen}
        onCancel={() => setDeleteErrorOpen(false)}
        footer={null}
        width={420}
        centered
        closable={false}
      >
        <div style={{ textAlign: "center", padding: "0px 14px 8px" }}>
          <div style={{
            width: 35, height: 35, borderRadius: "50%",
            background: "#ff4d4f", display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 20px",
            fontSize: 24, color: "white", fontWeight: "bold",
          }}>!</div>
          <p style={{ fontSize: 15, color: "#444", lineHeight: 1.6, marginBottom: 24 }}>
            You cannot delete this account because it has an existing balance.
            Only accounts with a zero balance can be deleted.
          </p>
          <Button
            onClick={() => setDeleteErrorOpen(false)}
            style={{ borderRadius: 6, width: 115, height: 34, borderColor: "black" }}
          >
            Got it
          </Button>
        </div>
      </StyledModal>

      <StyledDeleteModal
        open={deleteConfirmOpen}
        onCancel={() => { if (!isDeleting) setDeleteConfirmOpen(false); }}
        footer={null}
        width={420}
        centered
        closable={false}
      >
        <div style={{ textAlign: "center", padding: "32px 16px 8px" }}>
          <p style={{ fontSize: 15, color: "#444", marginBottom: 28 }}>
            Are you sure you want to delete this Account?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <ButtonOutlined
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={isDeleting}
              style={{ borderRadius: 6, width: 100, height: 38, borderColor: "black" }}
            >
              No
            </ButtonOutlined>
            <ButtonFilled

              onClick={confirmDelete}
              loading={isDeleting}
              style={{ borderRadius: 6, width: 100, height: 38, }}
            >
              Yes
            </ButtonFilled>
          </div>
        </div>
      </StyledDeleteModal>

      {showReport && (
        <AccountReport
          reportData={reportData}
          onClose={() => { setShowReport(false); setReportData(null); }}
        />
      )}
    </>
  );
};

export default Accounts;